/**
 * Shared chrome for the four self-serve screens: wordmark, step indicator and
 * the card the content sits in.
 *
 * The step indicator is a status display, not navigation. Steps are ordered and
 * the router enforces that, so offering a click back to a completed step would
 * present a control that either does nothing or drops the visitor into a screen
 * whose answers have already been consumed. It reports position; it does not
 * pretend to move you.
 */

import { ReactNode } from 'react';
import styles from './index.module.css';

export const STEPS = ['Qualify', 'Your details', 'Try it', 'Pay'] as const;

interface Props {
  /** 1-based, matching the numbers shown to the visitor. */
  step: 1 | 2 | 3 | 4;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Rendered under the card, outside the panel padding. */
  footer?: ReactNode;
}

const FlowShell = ({ step, title, subtitle, children, footer }: Props) => (
  <div className={styles.wrap}>
    <div className={styles.wordmark}>AVRIO</div>
    <p className={styles.tagline}>
      Self-serve for small and mid-size teams — get your rate, try it on a real
      job description, then activate when you are ready.
    </p>

    <ol className={styles.steps} aria-label="Progress">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const state = n === step ? 'current' : n < step ? 'done' : 'upcoming';
        return (
          <li
            key={label}
            className={`${styles.step} ${styles[state]}`}
            aria-current={n === step ? 'step' : undefined}
          >
            <span className={styles.stepNum}>{n}</span>
            {label}
          </li>
        );
      })}
    </ol>

    <section className={styles.panel}>
      <header className={styles.panelHead}>
        <h1 className={styles.title}>{title}</h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </header>
      <div className={styles.panelBody}>{children}</div>
    </section>

    {footer}
  </div>
);

export default FlowShell;
